import React, { useCallback, useRef, useContext } from 'react';
import serialize from 'form-serialize';

import { AppContext } from '@prisjakt/ui-app-context';

import { Spinner } from 'src/components/spinner';
import { ButtonSize } from '@prisjakt/ui/button';

import { ShopReviewFormData } from 'src/areas/reviews/types';

import * as S from './styles';

export enum ActionType {
  CREATE = 'create',
  EDIT = 'edit',
  PUBLISH = 'publish',
}

type Props = {
  className?: string;
  children: React.ReactNode;
  id?: string;
  onSubmit: (formData: ShopReviewFormData) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  pending?: boolean;
  actionType: ActionType;
  onChange?: (formData: ShopReviewFormData) => void;
};

const ReviewForm = ({
  className,
  children,
  id,
  onSubmit,
  onCancel,
  onDelete,
  pending,
  actionType,
  onChange,
}: Props) => {
  const { t } = useContext(AppContext);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const formData = serialize(formRef.current, { hash: true });
      onSubmit(formData);
    },
    [onSubmit]
  );

  const handleChange = useCallback(() => {
    if (onChange) {
      const formData = serialize(formRef.current, { hash: true });
      onChange(formData);
    }
  }, [onChange]);

  const submitButtonText = () => {
    switch (actionType) {
      case 'create':
        return t('account', 'forgot-password.submit-button');
      case 'publish':
        return t('reviews', 'dialog.publish');
      default:
        return t('reviews', 'dialog.update');
    }
  };

  return (
    <S.Form
      className={className}
      method="post"
      id={id}
      ref={formRef}
      onSubmit={handleSubmit}
      onChange={handleChange}
    >
      {children}
      <S.Buttons>
        {onDelete && (
          <S.DeleteButton onClick={onDelete}>
            {t('reviews', 'dialog.delete')}
          </S.DeleteButton>
        )}
        {onCancel && (
          <S.CancelButton size={ButtonSize.SMALL} onClick={onCancel}>
            {t('common', 'button.cancel')}
          </S.CancelButton>
        )}

        <S.SubmitButton
          htmlType="submit"
          size={ButtonSize.SMALL}
          disabled={pending}
        >
          {pending ? (
            <Spinner isActive={pending} position="none" />
          ) : (
            submitButtonText()
          )}
        </S.SubmitButton>
      </S.Buttons>
    </S.Form>
  );
};

export default ReviewForm;
